import { createContext, ReactElement, useEffect, useReducer } from 'react';

// third party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

// types
import { KeyedObject } from 'types';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';

import { AuthenticationService } from '../api-client/services/AuthenticationService';
import { setAuthToken } from '../api-client/config';
import { OpenAPI } from '../api-client/core/OpenAPI';

import { defaultSession, useSession } from '../SessionContext';


import { StateService } from 'api-client/services/StateService';
import { useLogger } from '../components/Logger';
import { MessProjectsService } from '@api-client';

const chance = new Chance();

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

function verifyToken(serviceToken: string): boolean {
  if (!serviceToken) {
    return false;
  }

  const decoded: KeyedObject = jwtDecode(serviceToken);

  // Ensure 'exp' exists and compare it to the current timestamp
  if (!decoded.exp) {
    throw new Error("Token does not contain 'exp' property.");
  }

  return decoded.exp > Date.now() / 1000;
}

function persistServiceToken(serviceToken?: string | null): void {
  setAuthToken(serviceToken ?? null);

  if (serviceToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export function JWTProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);
  const { session, setSession: setAppSession } = useSession();
  const logger = useLogger();

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = typeof OpenAPI.TOKEN === 'function'
          ? await OpenAPI.TOKEN({} as any)
          : OpenAPI.TOKEN;

        if (serviceToken && verifyToken(serviceToken)) {
          persistServiceToken(serviceToken);
          setAppSession({ ...(session ?? defaultSession), token: serviceToken });
          const response = await axios.get('/api/account/me');
          const { user } = response.data;
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: LOGOUT
        });
      }
    };

    init();
  }, []);

  const login = async (email: string, password: string) => {
    const user = {
      user_id: email,
      password,
    };


    const response = await AuthenticationService.login(user);
    if (response?.access_token) {
      persistServiceToken(response.access_token);
    }

    const saved_session = undefined;// await StateService.getUserState(email);

    const last_session = await StateService.getLastSession().catch((error) => {
      logger.error('Failed to get last session for user:', email, error);
    });

    if (last_session && last_session.messproject_id) {
      logger.debug('Found last session for user:', email, 'with messproject_id:', last_session.messproject_id);

      MessProjectsService.getMessproject(String(last_session.messproject_id)).then((messproject) => {
        logger.debug('Fetched messproject for last session:', messproject);
        setAppSession({
          ...(session ?? saved_session ?? defaultSession),
          token: response.access_token,
          is_admin: response.is_admin,
          current_messproject: messproject,
          user: {
            name: email,
            email: email
          }
        });
      }).catch((error) => {
        logger.error('Failed to fetch messproject for last session:', error);
        logger.debug('No saved session found for user:', email, 'Initializing new session', "token:", response.access_token);
        setAppSession({
          ...(session ?? defaultSession),
          token: response.access_token,
          is_admin: response.is_admin,
          user: {
            name: email,
            email: email
          }
        });
        logger.debug('New session initialized:', session);
      });



      // You can add additional logic here to restore the session based on the messproject_id
    } else {
      logger.debug('No last session found for user:', email);
      setAppSession({
          ...(session ?? defaultSession),
          token: response.access_token,
          is_admin: response.is_admin,
          user: {
            name: email,
            email: email
          }
        });
    }




    logger.debug('Login session:', session); // Debug log to check the login response
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: {
          name: email,
          email: email
        }
      }
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers!),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = async () => {
    await AuthenticationService.logoutApiAuthLogoutPost();
    persistServiceToken(null);
    setAppSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => { };

  const updateProfile = () => { };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext>;
}

export default JWTContext;
